import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DollarSign,
  Edit,
  Eye,
  EyeOff,
  FileSpreadsheet,
  GripVertical,
  Image,
  MoveVertical,
  Package2,
  PackageCheck,
  PackageX,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useProducts } from "../../hooks/useProducts";
import { formatPrice } from "../../utils/format";
import { AddProductPannel } from "./AddProductPannel";
import { ProductsStockPannel } from "./ProductsStockPannel";

// Product type imported from reusable hook

interface Category {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export function ProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);

  // Categories state
  const [categorySearch, setCategorySearch] = React.useState("");
  const [isCreatingCategory, setIsCreatingCategory] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState<Category | null>(
    null
  );
  const [categoryFormData, setCategoryFormData] = React.useState({
    name: "",
    slug: "",
  });

  // Reorder categories state (placed after categories query to avoid TDZ)

  // Fetch products via reusable hook
  const { data: products, isLoading } = useProducts();

  // Case-insensitive client-side filtering by product name
  const filteredProducts = React.useMemo(() => {
    const list = products || [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((p) => (p.name || "").toLowerCase().includes(term));
  }, [products, search]);

  // Fetch product statistics
  const { data: stats } = useQuery({
    queryKey: ["product-stats"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/products/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json() as Promise<{
        totalProducts: number;
        totalValue: number;
        lowStock: number;
        outOfStock: number;
      }>;
    },
  });

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      try {
        const response = await fetch("/api/carti/categorii");
        if (!response.ok) {
          throw new Error("Eroare la încărcarea categoriilor");
        }
        const result = await response.json();
        console.log("Categories API response:", result);

        // Handle the actual API response structure: { status, mesaj, date }
        if (result && result.date && Array.isArray(result.date)) {
          return result.date as Category[];
        }

        // Fallback for different response structures
        if (result && result.data && Array.isArray(result.data)) {
          return result.data as Category[];
        }

        // If data structure is different, try to handle it
        if (Array.isArray(result)) {
          return result as Category[];
        }

        // If no data, return empty array
        return [] as Category[];
      } catch (error) {
        console.error("Categories fetch error:", error);
        // Return empty array on error to prevent undefined
        return [] as Category[];
      }
    },
  });

  // Reorder categories state (depends on categories)
  const [reorderOpen, setReorderOpen] = React.useState(false);
  const [localCategoryOrder, setLocalCategoryOrder] = React.useState<
    Category[]
  >([]);
  React.useEffect(() => {
    if (categories && Array.isArray(categories)) {
      setLocalCategoryOrder(categories);
    }
  }, [categories]);

  const moveCategory = (fromIndex: number, toIndex: number) => {
    setLocalCategoryOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const reorderCategories = useMutation({
    mutationFn: async (orderIds: number[]) => {
      const response = await fetchWithAuth(`/api/admin/categories/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: orderIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la reordonarea categoriilor");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success("Ordinea categoriilor a fost actualizată");
      setReorderOpen(false);
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  console.log("categories", categories);

  // Delete product mutation
  const deleteProduct = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 400) {
          throw new Error(
            data.message ||
              "Nu se poate șterge produsul deoarece există comenzi asociate"
          );
        }
        throw new Error("Eroare la ștergerea produsului");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produs șters cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Toggle product visibility mutation
  const toggleVisibility = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${id}/toggle-visibility`,
        {
          method: "PATCH",
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Eroare la schimbarea vizibilității produsului"
        );
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Category mutations
  const createCategory = useMutation({
    mutationFn: async (data: typeof categoryFormData) => {
      const response = await fetchWithAuth("/api/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la crearea categoriei");
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie creată cu succes");
      setIsCreatingCategory(false);
      setCategoryFormData({ name: "", slug: "" });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<typeof categoryFormData>;
    }) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Eroare la actualizarea categoriei"
        );
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie actualizată cu succes");
      setEditingCategory(null);
      setCategoryFormData({ name: "", slug: "" });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la ștergerea categoriei");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie ștearsă cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Sigur doriți să ștergeți acest produs? Această acțiune nu poate fi anulată."
      )
    ) {
      deleteProduct.mutate(id);
    }
  };

  // Visibility handlers
  const handleToggleVisibility = (id: number) => {
    toggleVisibility.mutate(id);
  };

  // Category handlers
  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategory.mutate({
        id: editingCategory.id,
        data: categoryFormData,
      });
    } else {
      createCategory.mutate(categoryFormData);
    }
  };

  const handleCategoryEdit = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
    });
    setIsCreatingCategory(true);
  };

  const handleCategoryDelete = (id: number) => {
    if (
      window.confirm(
        "Sigur doriți să ștergeți această categorie? Această acțiune nu poate fi anulată."
      )
    ) {
      deleteCategory.mutate(id);
    }
  };

  const handleCancelCategoryForm = () => {
    setIsCreatingCategory(false);
    setEditingCategory(null);
    setCategoryFormData({ name: "", slug: "" });
  };

  // Filter categories based on search
  const filteredCategories = React.useMemo(() => {
    if (!categories || !Array.isArray(categories)) {
      return [];
    }
    return categories.filter((category) =>
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [categories, categorySearch]);

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Produse</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="w-4 h-4" />
            Adaugă Produs
          </Button>
          <Button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={async () => {
              try {
                const response = await fetchWithAuth(
                  "/api/admin/products/export",
                  {
                    method: "GET",
                    headers: {
                      Accept:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                  }
                );
                if (!response.ok) {
                  throw new Error("Eroare la exportul produselor");
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `produse-${
                  new Date().toISOString().split("T")[0]
                }.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error("Export error:", error);
              }
            }}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Package2 className="w-4 h-4" />
              Total Produse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.totalProducts || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Valoare Totală Stoc
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats?.totalValue || 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <PackageCheck className="w-4 h-4" />
              Stoc Redus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.lowStock || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <PackageX className="w-4 h-4" />
              Stoc Epuizat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.outOfStock || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Product Form */}
      {isCreating && (
        <AddProductPannel
          onSuccess={() => setIsCreating(false)}
          onCancel={() => setIsCreating(false)}
        />
      )}

      {/* Stocks Panel */}
      <ProductsStockPannel />

      {/* Categories Management Panel */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package2 className="w-5 h-5 text-purple-600" />
              Categorii Cărți
            </div>
            <div className="flex gap-2">
              <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <MoveVertical className="w-4 h-4" />
                    Reordonează
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Reordonare categorii</DialogTitle>
                    <DialogDescription>
                      Mută categoriile sus/jos pentru a schimba ordinea.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="max-h-[60vh] overflow-auto space-y-2">
                    {localCategoryOrder.length === 0 ? (
                      <div className="text-sm text-gray-500">
                        Nu există categorii
                      </div>
                    ) : (
                      localCategoryOrder.map((cat, idx) => (
                        <div
                          key={cat.id}
                          className="flex items-center justify-between rounded border p-2 bg-white"
                        >
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{cat.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                idx > 0 && moveCategory(idx, idx - 1)
                              }
                              disabled={idx === 0}
                            >
                              Sus
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                idx < localCategoryOrder.length - 1 &&
                                moveCategory(idx, idx + 1)
                              }
                              disabled={idx === localCategoryOrder.length - 1}
                            >
                              Jos
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setReorderOpen(false)}
                    >
                      Anulează
                    </Button>
                    <Button
                      onClick={() =>
                        reorderCategories.mutate(
                          localCategoryOrder.map((c) => c.id)
                        )
                      }
                      disabled={reorderCategories.isPending}
                    >
                      {reorderCategories.isPending
                        ? "Se salvează..."
                        : "Salvează ordinea"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                onClick={() => setIsCreatingCategory(true)}
              >
                <Plus className="w-4 h-4" />
                Adaugă Categorie
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Category Form */}
          {isCreatingCategory && (
            <Card className="bg-gray-50 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">
                  {editingCategory
                    ? "Editează Categorie"
                    : "Adaugă Categorie Nouă"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Nume Categorie
                      </label>
                      <Input
                        value={categoryFormData.name}
                        onChange={(e) =>
                          setCategoryFormData((f) => ({
                            ...f,
                            name: e.target.value,
                          }))
                        }
                        placeholder="ex: Spiritualitate"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Slug
                      </label>
                      <Input
                        value={categoryFormData.slug}
                        onChange={(e) =>
                          setCategoryFormData((f) => ({
                            ...f,
                            slug: e.target.value,
                          }))
                        }
                        placeholder="ex: spiritualitate"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Se va salva automat lowercase pe server
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelCategoryForm}
                    >
                      Anulează
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createCategory.isPending || updateCategory.isPending
                      }
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {createCategory.isPending || updateCategory.isPending
                        ? "Se salvează..."
                        : editingCategory
                        ? "Actualizează"
                        : "Salvează"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Category Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută categorii..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
            />
          </div>

          {/* Categories List */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">ID</TableHead>
                  <TableHead className="font-semibold">Nume</TableHead>
                  <TableHead className="font-semibold">Slug</TableHead>
                  <TableHead className="font-semibold">Data Creării</TableHead>
                  <TableHead className="font-semibold">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoriesLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="flex items-center justify-center text-gray-500">
                        Se încarcă categoriile...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="text-gray-500">
                        {categorySearch
                          ? "Nu există categorii care să se potrivească cu căutarea"
                          : "Nu există categorii"}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCategories.map((category) => (
                    <TableRow key={category.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        #{category.id}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900">
                          {category.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-purple-100 text-purple-800 ring-1 ring-purple-200">
                          {category.slug}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {new Date(category.createdAt).toLocaleDateString(
                          "ro-RO"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => handleCategoryEdit(category)}
                          >
                            <Edit className="w-4 h-4" />
                            Editează
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            onClick={() => handleCategoryDelete(category.id)}
                            disabled={deleteCategory.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                            Șterge
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Search and Quick Actions */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută după nume..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Quick Actions Info */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Acțiuni Rapide de Vizibilitate
              </span>
            </div>
            <span className="text-xs text-blue-600">
              Folosește butoanele din tabel pentru a schimba rapid vizibilitatea
              produselor
            </span>
          </div>
        </div>
      </Card>

      {/* Products Table */}
      <Card className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Nume</TableHead>
                <TableHead className="font-semibold">Preț</TableHead>
                <TableHead className="font-semibold">Stoc</TableHead>
                <TableHead className="font-semibold">Vizibilitate</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex items-center justify-center text-gray-500">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-gray-500">Nu există produse</div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">#{product.id}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {product.name}
                          </span>
                          {product.images && product.images.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-800 ring-1 ring-blue-200 text-xs">
                              <Image className="w-3 h-3 mr-1" />
                              {product.images.length}
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-gray-500 truncate max-w-md">
                          {product.description}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(product.price)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          product.stock > 10
                            ? "bg-green-100 text-green-800 ring-1 ring-green-200"
                            : product.stock > 0
                            ? "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200"
                            : "bg-red-100 text-red-800 ring-1 ring-red-200"
                        }
                      >
                        {product.stock} bucăți
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          product.hide
                            ? "bg-gray-100 text-gray-800 ring-1 ring-gray-200"
                            : "bg-green-100 text-green-800 ring-1 ring-green-200"
                        }
                      >
                        {product.hide ? (
                          <div className="flex items-center gap-1">
                            <EyeOff className="w-3 h-3" />
                            Ascuns
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Vizibil
                          </div>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          product.stock > 10
                            ? "bg-green-100 text-green-800 ring-1 ring-green-200"
                            : product.stock > 0
                            ? "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200"
                            : "bg-red-100 text-red-800 ring-1 ring-red-200"
                        }
                      >
                        {product.stock > 10
                          ? "În Stoc"
                          : product.stock > 0
                          ? "Stoc Limitat"
                          : "Stoc Epuizat"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Quick visibility toggle button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className={`flex items-center gap-1 ${
                            product.hide
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                              : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                          }`}
                          onClick={() => handleToggleVisibility(product.id)}
                          disabled={toggleVisibility.isPending}
                          title={
                            product.hide
                              ? "Afișează produsul"
                              : "Ascunde produsul"
                          }
                        >
                          {product.hide ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                          {product.hide ? "Afișează" : "Ascunde"}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={() => navigate(`/products/${product.id}/`)}
                        >
                          <Edit className="w-4 h-4" />
                          Editează
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(product.id)}
                          disabled={deleteProduct.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                          Șterge
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
